import React, { useCallback } from "react";
import { FaCalendarAlt, FaCar, FaTrash, FaCheckCircle, FaClock, FaExclamationTriangle } from "react-icons/fa";
import { MdLocalCarWash } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const MySubscriptionCard = ({
  subscription,
  onDelete,
  showDelete,
  styles = {},
}) => {
  const {
    client_subscription_id,
    subscription_name,
    subscription_features,
    subscription_cost,
    created_date,
    start_date,
    end_date,
    payment_status,
    subscribedVehicles,
    client_id,
    subscription_id
  } = subscription;

  // Mock translation functions for demo - replace with your actual useTranslation hook
  const t = (key) => key;
  const i18n = { 
    language: 'en', 
    dir: () => 'ltr' 
  };
  
  // Mock navigate function - replace with your actual useNavigate hook
  const navigate = useNavigate();

  const checkSubscriptionStatus = useCallback((endDate, createdDate) => {
    const today = new Date();
    const start = new Date(createdDate);
    const end = new Date(endDate);
    const timeDifference = end - today;
    const total = end - start;
    const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    const isExpired = daysRemaining < 0;
    const duration = Math.ceil(total / (1000 * 60 * 60 * 24));
    return {
      isExpired,
      daysRemaining: isExpired ? 0 : daysRemaining,
      duration,
    };
  }, []);

  const statusInfo = checkSubscriptionStatus(end_date, start_date);
  const { isExpired, daysRemaining, duration } = statusInfo;

  const handlePayment = () => {
    const subscriptionWithDuration = {
      ...subscription,
      duration_days: duration,
    };
    localStorage.setItem("actionType", "RENEW");
    navigate("/payment", { state: { subscription: subscriptionWithDuration } });
  };

  // Get status color and icon based on subscription state
  const getStatusInfo = () => {
    if (isExpired) {
      return {
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        icon: <FaExclamationTriangle />,
        text: t("expired")
      };
    } else if (daysRemaining <= 7) {
      return {
        color: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
        icon: <FaClock />,
        text: `${daysRemaining} ${t("days_left") || "days left"}`
      };
    } else {
      return {
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        icon: <FaCheckCircle />,
        text: `${daysRemaining} ${t("days_left") || "days left"}`
      };
    }
  };

  const status = getStatusInfo();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Parse subscription features safely
  const features = React.useMemo(() => {
    try {
      return JSON.parse(subscription_features) || {};
    } catch {
      return {};
    }
  }, [subscription_features]);

  if (!subscription) return null;

  return (
    <div className={`w-80 max-[450px]:w-72 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300 ${styles}`}>
      
      {/* Header with Status Badge */}
      <div className="relative bg-gradient-to-r from-white to-gray-100 text-black p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-bold leading-tight mb-2">
              {subscription_name?.toUpperCase()}
            </h2>
            
            {/* Status Badge */}
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${status.bgColor} ${status.color} border ${status.borderColor}`}>
              {status.icon}
              <span>{status.text}</span>
            </div>
          </div>
          
          {showDelete && (
            <button
              className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(subscription);
              }}
            >
              <FaTrash size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Expiry Date Section */}
      <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FaCalendarAlt className="text-gray-400" />
            <span>{t("valid_until") || "Valid until"}</span>
          </div>
          <div className="text-right">
            <div className="font-semibold text-gray-800">{formatDate(end_date)}</div>
            <div className="text-xs text-gray-500">
              {daysRemaining} {t("of") || "of"} {duration} {t("days") || "days"}
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                isExpired ? 'bg-red-500' : 
                daysRemaining <= 7 ? 'bg-orange-500' : 'bg-green-500'
              }`}
              style={{ 
                width: `${Math.max(5, (daysRemaining / duration) * 100)}%` 
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="p-5 space-y-4">
        
        {/* Usage Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
              <MdLocalCarWash size={18} />
            </div>
            <div className="font-bold text-gray-800">
              {features?.number_of_washes || t("unlimited")}
            </div>
            <div className="text-xs text-gray-500">{t("washes") || "Washes"}</div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
              <FaCar size={16} />
            </div>
            <div className="font-bold text-gray-800">
              {subscribedVehicles?.length || 0} / {features?.number_of_cars || 0}
            </div>
            <div className="text-xs text-gray-500">{t("vehicles") || "Vehicles"}</div>
          </div>
        </div>

        {/* Services */}
        {features?.subscriptionServices?.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              {t("services") || "Included Services"}
            </h3>
            
            <div className="space-y-2">
              {features.subscriptionServices.slice(0, 3).map((service, index) => (
                <div key={index} className="flex items-center gap-3 text-sm">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-700 truncate">
                    {i18n.dir() === "rtl"
                      ? service.serviceType.name_ar
                      : service.serviceType.name_en}
                  </span>
                </div>
              ))}
              
              {features.subscriptionServices.length > 3 && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
                  <span className="text-blue-600 font-medium">
                    +{features.subscriptionServices.length - 3} {t("more") || "more services"}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
        
        {/* Payment Status & Price */}
        <div className="flex items-center justify-between">
          <div className="text-sm">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              payment_status === "Active" ? "bg-green-100 text-green-800" :
              payment_status === "Pending" ? "bg-yellow-100 text-yellow-800" :
              "bg-gray-100 text-gray-800"
            }`}>
              {payment_status}
            </span>
          </div>
          <span className="font-bold text-lg text-gray-800">
            {parseFloat(subscription_cost).toFixed(0)} QAR
          </span>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          {(payment_status === "Pending" || isExpired) && (
            <button
              onClick={handlePayment}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-medium text-sm transition-colors"
            >
              {payment_status === "Pending" ? t("PAY NOW") : t("RENEW")}
            </button>
          )}
          
          {/* <button
            onClick={() => onDelete(client_subscription_id)}
            className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2.5 rounded-xl font-medium text-sm transition-colors"
          >
            {t("cancel")}
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default MySubscriptionCard;